// src/components/NewsletterSection.js
// Attractive lead-capture section for embedding on Home / About pages.
// Wraps the reusable <LeadForm /> with engaging copy + social proof.
import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FaBolt, FaBell, FaShieldAlt, FaUsers } from 'react-icons/fa';
import LeadForm from './LeadForm';

const N  = '#0d2235';
const E  = '#29ABE2';
const Eo = '#1a8fc0';
const MT = '#5a7a8f';
const GREEN = '#16c784';

const pulse = keyframes`
  0%,100% { opacity: 1;   transform: scale(1);   }
  50%     { opacity: 0.6; transform: scale(0.85); }
`;

const Wrap = styled.section`
  background: transparent !important;
  position: relative;
  overflow: hidden;
`;

const Inner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 940px) {
    grid-template-columns: 1fr;
    gap: 2.6rem;
  }
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 6px 14px;
  border: 1.5px solid rgba(41,171,226,0.30);
  border-radius: 9999px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${Eo};
  background: rgba(41,171,226,0.10);
  margin-bottom: 1.1rem;

  .dot { width: 7px; height: 7px; border-radius: 50%; background: ${GREEN}; animation: ${pulse} 1.7s ease-in-out infinite; }
`;

const Title = styled.h2`
  font-size: clamp(1.9rem, 4vw, 3rem) !important;
  font-weight: 800;
  line-height: 1.05 !important;
  letter-spacing: -0.02em;
  color: ${N} !important;
  -webkit-text-fill-color: ${N} !important;
  margin: 0 0 1rem;

  em { font-style: normal; color: ${E} !important; -webkit-text-fill-color: ${E} !important; }
`;

const Sub = styled.p`
  font-size: 1.05rem;
  color: ${MT} !important;
  line-height: 1.7;
  max-width: 480px;
  margin: 0 0 1.8rem;
`;

const Perks = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.9rem 1.4rem;
  margin-bottom: 1.8rem;

  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const Perk = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;

  .ic {
    width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: rgba(41,171,226,0.12); color: ${E}; font-size: 0.95rem;
  }
  h4 { font-size: 0.92rem !important; font-weight: 700; color: ${N} !important; margin: 0 0 0.1rem; }
  p  { font-size: 0.82rem; color: ${MT} !important; line-height: 1.5; margin: 0; }
`;

const Proof = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.86rem;
  font-weight: 600;
  color: ${N};

  .av { display: flex; }
  .av span {
    width: 26px; height: 26px; border-radius: 50%;
    border: 2px solid #fff; margin-left: -8px;
    background: linear-gradient(135deg, #29ABE2, #7c6cff);
  }
  .av span:first-child { margin-left: 0; }
  b { color: ${E}; }
  .muted { color: ${MT}; font-weight: 500; }
`;

const DEFAULT_PERKS = [
  { ic: <FaBolt />,      t: 'Weekly AI insights',   d: 'BTC & altcoin trends, signal breakdowns.' },
  { ic: <FaBell />,      t: 'Early feature access',  d: 'New tools & TGEN drops before anyone.' },
  { ic: <FaShieldAlt />, t: 'No spam, ever',         d: 'One-click unsubscribe. Email never shared.' },
  { ic: <FaUsers />,     t: 'Join the community',    d: '50,000+ traders already onboard.' },
];

const fUp = {
  hidden:  { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5, ease: 'easeOut' } }),
};

function NewsletterSection({
  source = 'website',
  eyebrow = 'Free newsletter',
  title = <>Stay ahead of <em>every crypto move.</em></>,
  subtitle = "Join 50,000+ traders getting AI-powered market insights, signal breakdowns, and early feature access — straight to their inbox. Free, no noise.",
  perks = DEFAULT_PERKS,
  ctaLabel = 'Subscribe free',
}) {
  return (
    <Wrap>
      <Inner>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fUp}>
          <Eyebrow><span className="dot" /> {eyebrow}</Eyebrow>
          <Title>{title}</Title>
          <Sub>{subtitle}</Sub>

          <Perks>
            {perks.map((p, i) => (
              <motion.div key={p.t} custom={i + 1} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fUp}>
                <Perk>
                  <div className="ic">{p.ic}</div>
                  <div>
                    <h4>{p.t}</h4>
                    <p>{p.d}</p>
                  </div>
                </Perk>
              </motion.div>
            ))}
          </Perks>

          <Proof>
            <span className="av"><span /><span /><span /><span /></span>
            <span><b>50K+</b> <span className="muted">traders already subscribed</span></span>
          </Proof>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.55, ease: 'easeOut' }}>
          <LeadForm source={source} compact ctaLabel={ctaLabel} />
        </motion.div>
      </Inner>
    </Wrap>
  );
}

export default NewsletterSection;
